'use client';

import { useTranslations, useLocale } from 'next-intl';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'next/navigation';
import { Link, usePathname, useRouter } from '../../../i18n/routing';
import { Scale, Globe } from 'lucide-react';

export function TheFooter() {
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const params = useParams();
  
  const [legalOpen, setLegalOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  
  const legalRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    // Click outside handler - use click instead of mousedown to allow links to work
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Don't close if clicking on a link inside the menu
      if (target instanceof Element && target.closest('a')) {
        return;
      }
      
      if (legalRef.current && !legalRef.current.contains(target)) {
        setLegalOpen(false);
      }
      if (langRef.current && !langRef.current.contains(target)) {
        setLangOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const currentYear = new Date().getFullYear();

  const switchLocale = (newLocale: string) => {
    // Store user's explicit language choice
    if (typeof window !== 'undefined') {
      localStorage.setItem('user_language_choice', newLocale);
    }
    // @ts-expect-error -- We know the params match the route requirements
    router.replace({ pathname, params }, { locale: newLocale });
    setLangOpen(false);
  };

  return (
    <footer className="relative z-20 px-3 py-2 bg-white/85 backdrop-blur-[14px] border-t border-white/45 dark:bg-slate-900/85 dark:border-slate-700">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          &copy; {currentYear} {t('common.appName')}. {t('common.rightsReserved')}
        </p>

        <div className="flex gap-2 flex-wrap justify-center">
          {/* Legal Menu */}
          <div className="relative" ref={legalRef}>
            <button
              onClick={() => setLegalOpen(!legalOpen)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:bg-blue-50 px-2 py-1 rounded dark:text-blue-400 dark:hover:bg-slate-800"
            >
              {mounted && <Scale size={16} />}
              {t('footer.legal')}
            </button>
            {legalOpen && (
              <div className="absolute bottom-full mb-1 left-0 bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-slate-200 dark:border-slate-700 py-1 min-w-[150px] z-[100]">
                <Link 
                  href="/terms" 
                  className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 no-underline"
                  onClick={() => setLegalOpen(false)}
                >
                  {t('termsPage.title')}
                </Link>
                <Link 
                  href="/privacy" 
                  className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 no-underline"
                  onClick={() => setLegalOpen(false)}
                >
                  {t('privacyPage.title')}
                </Link>
                <Link 
                  href="/cookies" 
                  className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 no-underline"
                  onClick={() => setLegalOpen(false)}
                >
                  {t('cookiesPage.title')}
                </Link>
              </div>
            )}
          </div>

          {/* Language Menu */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:bg-blue-50 px-2 py-1 rounded dark:text-blue-400 dark:hover:bg-slate-800"
            >
              {mounted && <Globe size={16} />}
              {locale.toUpperCase()}
            </button>
            {langOpen && (
              <div className="absolute bottom-full mb-1 right-0 bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-slate-200 dark:border-slate-700 py-1 min-w-[100px] z-[100]">
                <button
                  onClick={() => switchLocale('pl')}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 ${locale === 'pl' ? 'bg-blue-50 text-blue-600 dark:bg-slate-700 dark:text-blue-400' : 'text-slate-700 dark:text-slate-300'}`}
                >
                  Polski
                </button>
                <button
                  onClick={() => switchLocale('en')}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 ${locale === 'en' ? 'bg-blue-50 text-blue-600 dark:bg-slate-700 dark:text-blue-400' : 'text-slate-700 dark:text-slate-300'}`}
                >
                  English
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}

